import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FiKey,
  FiPlus,
  FiSearch,
  FiUser,
  FiPhone,
  FiHome,
  FiDollarSign,
  FiCalendar,
  FiTrash2,
  FiEdit2,
  FiRefreshCw,
  FiFileText,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import ToolsHeader from "./ToolsHeader";
import {
  listLeases,
  addLease,
  editLease,
  deleteLease,
  getLeaseNotes,
} from "../../api/tools/toolsApi";
import "./Tools.css";

const LeaseManagementTool = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  // Leases list state
  const [leases, setLeases] = useState([]);
  const [loadingLeases, setLoadingLeases] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Lease Notes state / Modal
  const [selectedLeaseForNotes, setSelectedLeaseForNotes] = useState(null);
  const [leaseNotes, setLeaseNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // Add / Edit Modal
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [leaseForm, setLeaseForm] = useState({
    id: null,
    client_name: "",
    client_mobile: "",
    property_name: "",
    rent_amount: "",
    deposit_amount: "",
    start_date: "",
    end_date: "",
  });
  const [submittingLease, setSubmittingLease] = useState(false);

  // 1. Fetch Leases
  const fetchAllLeases = useCallback(async () => {
    if (!token) return;
    setLoadingLeases(true);
    try {
      const data = await listLeases(token, {
        page_number: page,
        page_size: pageSize,
        order_by: "DESC",
      });

      const list =
        data?.leases ||
        data?.leaseAssets ||
        data?.data ||
        data?.content ||
        (Array.isArray(data) ? data : []);
      setLeases(list);

      if (data?.totalElements) {
        setTotalPages(Math.ceil(data.totalElements / pageSize));
      } else if (data?.totalPages || data?.totalPage) {
        setTotalPages(data.totalPages || data.totalPage);
      } else {
        setTotalPages(list.length < pageSize ? page : page + 1);
      }
    } catch (err) {
      console.error("Error fetching leases:", err);
      toast.error(err?.response?.data?.message || "Failed to load leases");
    } finally {
      setLoadingLeases(false);
    }
  }, [token, page, pageSize]);

  useEffect(() => {
    fetchAllLeases();
  }, [fetchAllLeases]);

  // 2. Fetch Lease Notes
  const handleOpenLeaseNotes = async (lease) => {
    setSelectedLeaseForNotes(lease);
    setLoadingNotes(true);
    try {
      const data = await getLeaseNotes(token, {
        lead_id: lease.id,
        page_number: 1,
        page_size: 50,
      });
      const list =
        data?.notes ||
        data?.leaseNotes ||
        data?.data ||
        (Array.isArray(data) ? data : []);
      setLeaseNotes(list);
    } catch (err) {
      console.error("Error fetching lease notes:", err);
      setLeaseNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  // 3. Add or Edit Lease
  const handleSaveLease = async (e) => {
    e.preventDefault();
    if (!leaseForm.client_name.trim() || !leaseForm.client_mobile.trim()) {
      toast.warning("Please provide client name and mobile number");
      return;
    }
    setSubmittingLease(true);
    try {
      if (leaseForm.id) {
        await editLease(token, {
          id: leaseForm.id,
          client_name: leaseForm.client_name,
          client_mobile: leaseForm.client_mobile,
          property_name: leaseForm.property_name,
          rent_amount: Number(leaseForm.rent_amount) || 0,
          deposit_amount: Number(leaseForm.deposit_amount) || 0,
          start_date: leaseForm.start_date,
          end_date: leaseForm.end_date,
        });
        toast.success("Lease updated successfully");
      } else {
        await addLease(token, {
          client_name: leaseForm.client_name,
          client_mobile: leaseForm.client_mobile,
          property_name: leaseForm.property_name,
          rent_amount: Number(leaseForm.rent_amount) || 0,
          deposit_amount: Number(leaseForm.deposit_amount) || 0,
          start_date: leaseForm.start_date,
          end_date: leaseForm.end_date,
        });
        toast.success("New lease registered successfully");
      }
      setShowLeaseModal(false);
      setLeaseForm({
        id: null,
        client_name: "",
        client_mobile: "",
        property_name: "",
        rent_amount: "",
        deposit_amount: "",
        start_date: "",
        end_date: "",
      });
      fetchAllLeases();
    } catch (err) {
      console.error("Error saving lease:", err);
      toast.error(err?.response?.data?.message || "Failed to save lease");
    } finally {
      setSubmittingLease(false);
    }
  };

  // 4. Delete Lease
  const handleDeleteLease = async (leaseId) => {
    if (!window.confirm("Are you sure you want to delete this lease record?"))
      return;
    try {
      await deleteLease(token, leaseId);
      toast.success("Lease deleted successfully");
      fetchAllLeases();
    } catch (err) {
      console.error("Error deleting lease:", err);
      toast.error(err?.response?.data?.message || "Failed to delete lease");
    }
  };

  // Filtered leases
  const filteredLeases = useMemo(() => {
    if (!searchQuery.trim()) return leases;
    const q = searchQuery.toLowerCase();
    return leases.filter(
      (l) =>
        (l.client_name && l.client_name.toLowerCase().includes(q)) ||
        (l.property_name && l.property_name.toLowerCase().includes(q)) ||
        (l.client_mobile && l.client_mobile.includes(q)) ||
        (l.title && l.title.toLowerCase().includes(q))
    );
  }, [leases, searchQuery]);

  // Aggregate stats
  const totalRentAmount = useMemo(() => {
    return leases.reduce(
      (sum, l) => sum + (Number(l.rent_amount || l.budget || 0) || 0),
      0
    );
  }, [leases]);

  const totalDepositAmount = useMemo(() => {
    return leases.reduce(
      (sum, l) => sum + (Number(l.deposit_amount || 0) || 0),
      0
    );
  }, [leases]);

  return (
    <div className="tools-container">
      <ToolsHeader
        title="Lease Management Tool"
        subtitle="Track commercial and residential leases, monthly rent schedules, security deposits, and tenant notes."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              className="tools-btn tools-btn--secondary"
              onClick={fetchAllLeases}
              disabled={loadingLeases}
            >
              <FiRefreshCw className={loadingLeases ? "spin" : ""} /> Refresh
            </button>
            <button
              className="tools-btn tools-btn--primary"
              onClick={() => {
                setLeaseForm({
                  id: null,
                  client_name: "",
                  client_mobile: "",
                  property_name: "",
                  rent_amount: "",
                  deposit_amount: "",
                  start_date: "",
                  end_date: "",
                });
                setShowLeaseModal(true);
              }}
            >
              <FiPlus /> New Lease
            </button>
          </div>
        }
      />

      {/* KPI Stats */}
      <div className="tools-stats-grid">
        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--blue">
            <FiKey />
          </div>
          <div>
            <div className="tools-stat-card__label">Active Leases</div>
            <div className="tools-stat-card__value text-primary">{leases.length}</div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--green">
            <FiDollarSign />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Monthly Rent</div>
            <div className="tools-stat-card__value text-success">
              ₹{Number(totalRentAmount).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--purple">
            <FiDollarSign />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Security Deposits</div>
            <div className="tools-stat-card__value text-dark">
              ₹{Number(totalDepositAmount).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="tools-action-bar">
        <div className="tools-search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search leases by tenant, property, mobile..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <span className="text-muted small">
          Showing {filteredLeases.length} of {leases.length} lease record(s)
        </span>
      </div>

      {/* Leases Table / List */}
      {loadingLeases ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading lease records...</p>
        </div>
      ) : filteredLeases.length === 0 ? (
        <div className="tools-empty-state">
          <div className="tools-empty-state__icon">
            <FiKey />
          </div>
          <h4>No Leases Found</h4>
          <p>Register your first property lease agreement with rent and deposit details.</p>
          <button
            className="tools-btn tools-btn--primary"
            onClick={() => {
              setLeaseForm({
                id: null,
                client_name: "",
                client_mobile: "",
                property_name: "",
                rent_amount: "",
                deposit_amount: "",
                start_date: "",
                end_date: "",
              });
              setShowLeaseModal(true);
            }}
          >
            <FiPlus /> Add New Lease
          </button>
        </div>
      ) : (
        <>
          <div className="tools-table-card">
            <div className="tools-table-responsive">
              <table className="tools-table">
                <thead>
                  <tr>
                    <th>Tenant / Client</th>
                    <th>Property</th>
                    <th>Monthly Rent</th>
                    <th>Security Deposit</th>
                    <th>Lease Period</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeases.map((lease) => (
                    <tr key={lease.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: "#eff6ff",
                              color: "#2563eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {lease.client_name
                              ? lease.client_name.charAt(0).toUpperCase()
                              : "T"}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {lease.client_name || lease.title || `Lease #${lease.id}`}
                            </div>
                            <div className="text-muted small d-flex align-items-center gap-1">
                              <FiPhone size={11} /> {lease.client_mobile || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1 text-dark fw-semibold">
                          <FiHome size={14} className="text-primary flex-shrink-0" />
                          <span>
                            {lease.property_name ||
                              [lease.city, lease.state].filter(Boolean).join(", ") ||
                              "Property N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="fw-bold text-success">
                          ₹
                          {Number(
                            lease.rent_amount || lease.budget || 0
                          ).toLocaleString("en-IN")}
                          /mo
                        </span>
                      </td>
                      <td>
                        <span className="text-secondary fw-semibold">
                          {lease.deposit_amount
                            ? `₹${Number(lease.deposit_amount).toLocaleString("en-IN")}`
                            : "—"}
                        </span>
                      </td>
                      <td>
                        <div className="small text-secondary d-flex align-items-center gap-1">
                          <FiCalendar size={13} className="text-muted" />
                          <span>
                            {lease.start_date || "—"} &rarr; {lease.end_date || "—"}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <button
                            className="tools-btn tools-btn--sm tools-btn--secondary"
                            onClick={() => handleOpenLeaseNotes(lease)}
                          >
                            <FiFileText size={13} /> Notes
                          </button>
                          <button
                            className="tools-btn--icon"
                            title="Edit Lease"
                            onClick={() => {
                              setLeaseForm({
                                id: lease.id,
                                client_name: lease.client_name || "",
                                client_mobile: lease.client_mobile || "",
                                property_name: lease.property_name || "",
                                rent_amount: lease.rent_amount || lease.budget || "",
                                deposit_amount: lease.deposit_amount || "",
                                start_date: lease.start_date || "",
                                end_date: lease.end_date || "",
                              });
                              setShowLeaseModal(true);
                            }}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="tools-btn--icon tools-btn--icon-danger"
                            title="Delete Lease"
                            onClick={() => handleDeleteLease(lease.id)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                <FiChevronLeft /> Previous
              </Button>
              <span className="small text-muted fw-semibold">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <FiChevronRight />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Modal: Add/Edit Lease */}
      <Modal show={showLeaseModal} onHide={() => setShowLeaseModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {leaseForm.id ? "Edit Lease Agreement" : "Add New Lease Agreement"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveLease}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Tenant / Client Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Rohan Gupta"
                    value={leaseForm.client_name}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, client_name: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Client Mobile Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={leaseForm.client_mobile}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, client_mobile: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </div>

              <div className="col-12">
                <Form.Group>
                  <Form.Label className="fw-semibold">Property Name / Unit</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Skyline Heights 402, Sector 128"
                    value={leaseForm.property_name}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, property_name: e.target.value })
                    }
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Monthly Rent (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 35000"
                    value={leaseForm.rent_amount}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, rent_amount: e.target.value })
                    }
                    min="0"
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Security Deposit (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 70000"
                    value={leaseForm.deposit_amount}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, deposit_amount: e.target.value })
                    }
                    min="0"
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Lease Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={leaseForm.start_date}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, start_date: e.target.value })
                    }
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Lease End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={leaseForm.end_date}
                    onChange={(e) =>
                      setLeaseForm({ ...leaseForm, end_date: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowLeaseModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingLease}>
              {submittingLease ? "Saving..." : leaseForm.id ? "Update Lease" : "Save Lease"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Lease Notes */}
      <Modal
        show={Boolean(selectedLeaseForNotes)}
        onHide={() => setSelectedLeaseForNotes(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            Lease Notes: {selectedLeaseForNotes?.client_name || selectedLeaseForNotes?.property_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingNotes ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" variant="primary" />
              <p className="small text-muted mt-2">Loading notes...</p>
            </div>
          ) : leaseNotes.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FiFileText size={36} className="mb-2 text-muted" />
              <p className="mb-0">No notes recorded for this lease.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {leaseNotes.map((n, idx) => (
                <div
                  key={n.id || idx}
                  className="p-3 bg-light rounded-3 border text-dark small"
                >
                  <p className="mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    {n.note || n.comment || JSON.stringify(n)}
                  </p>
                  {n.created_on && (
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {new Date(n.created_on).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedLeaseForNotes(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeaseManagementTool;
